import { motion, useReducedMotion } from 'framer-motion';
import { useAppState } from '../hooks/useAppState';
import { useRef, useCallback } from 'react';

const easeOut = [0.22, 1, 0.36, 1] as const;

export function SplashPage(): JSX.Element {
  const { navigate } = useAppState();
  const shouldReduceMotion = useReducedMotion();
  const touchStartY = useRef<number>(0);

  const handleNavigate = useCallback(() => {
    navigate('identity');
  }, [navigate]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const deltaY = touchStartY.current - e.changedTouches[0].clientY;
    if (deltaY > 60) {
      handleNavigate();
    }
  }, [handleNavigate]);

  return (
    <div
      onClick={handleNavigate}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'relative',
        width: '100%',
        height: '100dvh',
        background: 'radial-gradient(ellipse 80% 60% at 50% 40%, #ffffff 0%, #f5f5f7 60%, #f0f0f2 100%)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      {/* Subtle ambient glow */}
      <div
        style={{
          position: 'absolute',
          top: '30%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,0,0,0.02) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Center content */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
        }}
      >
        {/* Logo mark */}
        <motion.div
          initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: easeOut }}
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '14px',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 600,
            color: '#1a1a1c',
            letterSpacing: '0.08em',
            backdropFilter: 'blur(8px)',
          }}
        >
          OC
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={shouldReduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: easeOut }}
          style={{
            margin: 0,
            fontSize: '28px',
            fontWeight: 500,
            color: '#1a1a1c',
            textAlign: 'center',
            letterSpacing: '0.04em',
            lineHeight: 1.2,
          }}
        >
          你的工作，可以这么玩
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={shouldReduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: easeOut }}
          style={{
            margin: 0,
            fontSize: '15px',
            color: '#86868b',
            textAlign: 'center',
            lineHeight: 1.6,
            fontWeight: 400,
            letterSpacing: '0.02em',
            maxWidth: '280px',
          }}
        >
          发现 AI 让白领工作的 16 种新方式
        </motion.p>
      </div>

      {/* Bottom swipe hint */}
      <motion.div
        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.6, ease: easeOut }}
        style={{
          position: 'absolute',
          bottom: '52px',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '10px',
          userSelect: 'none',
        }}
      >
        <span
          style={{
            fontSize: '12px',
            color: '#c7c7cc',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            fontWeight: 500,
          }}
        >
          上滑或点击开始
        </span>
        <motion.div
          animate={shouldReduceMotion ? undefined : { y: [0, -5, 0] }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          style={{
            width: '20px',
            height: '32px',
            borderRadius: '10px',
            border: '1.5px solid rgba(0, 0, 0, 0.1)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: '6px',
          }}
        >
          <motion.div
            animate={shouldReduceMotion ? undefined : { y: [0, 8, 0], opacity: [0.5, 0.15, 0.5] }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            style={{
              width: '3px',
              height: '3px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            }}
          />
        </motion.div>
      </motion.div>
    </div>
  );
}
